import { useState, useRef } from "react";
import { X, Check, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** Plain string, or a labelled value (selected/onChange always use the value). */
type OptionItem = string | { label: string; value: string };

interface SearchableMultiSelectProps {
  options: OptionItem[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  allowCustom?: boolean;
}

export function SearchableMultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select items...",
  searchPlaceholder = "Search...",
  allowCustom = false,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const getLabel = (item: OptionItem) => typeof item === "string" ? item : item.label;
  const getValue = (item: OptionItem) => typeof item === "string" ? item : item.value;
  const labelFor = (value: string) => {
    const opt = options.find((o) => getValue(o) === value);
    return opt ? getLabel(opt) : value;
  };

  const filtered = options.filter(
    (o) =>
      getLabel(o).toLowerCase().includes(search.toLowerCase()) &&
      !selected.includes(getValue(o))
  );

  const showAddCustom =
    allowCustom &&
    search.trim() &&
    !options.some((o) => getLabel(o).toLowerCase() === search.trim().toLowerCase()) &&
    !selected.some((s) => s.toLowerCase() === search.trim().toLowerCase());

  const handleSelect = (value: string) => {
    onChange([...selected, value]);
    setSearch("");
    inputRef.current?.focus();
  };

  const handleRemove = (value: string) => {
    onChange(selected.filter((s) => s !== value));
  };

  const handleAddCustom = () => {
    const trimmed = search.trim();
    if (trimmed) {
      onChange([...selected, trimmed]);
      setSearch("");
      inputRef.current?.focus();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
      <div
        data-multi-select-anchor
        className={cn(
          "min-h-11 w-full rounded-lg border border-transparent bg-muted/60 px-3 py-2 text-sm cursor-text transition-colors focus-within:bg-background",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background",
          open && "ring-2 ring-ring ring-offset-2"
        )}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        <div className="flex flex-wrap gap-1.5">
          {selected.map((item) => (
            <Badge
              key={item}
              variant="secondary"
              className="gap-1 pr-1 text-xs font-medium max-w-full"
              title={labelFor(item)}
            >
              <span className="truncate min-w-0">{labelFor(item)}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(item);
                }}
                className="ml-0.5 shrink-0 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={selected.length === 0 ? placeholder : searchPlaceholder}
            className="flex-1 min-w-[120px] bg-transparent outline-none text-sm placeholder:text-muted-foreground py-0.5"
          />
        </div>
      </div>
      </PopoverAnchor>

      {(filtered.length > 0 || showAddCustom) && (
        <PopoverContent
          align="start"
          sideOffset={4}
          className="min-w-[var(--radix-popover-trigger-width)] w-max max-w-[min(28rem,90vw)] p-0 rounded-lg max-h-60 overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            // Clicking the anchor (input/chips) must not close the dropdown.
            const anchor = (e.target as HTMLElement | null)?.closest?.("[data-radix-popper-anchor], [data-multi-select-anchor]");
            if (anchor) e.preventDefault();
          }}
        >
          {filtered.map((item) => {
            const value = getValue(item);
            return (
              <button
                key={value}
                type="button"
                onClick={() => handleSelect(value)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-popover-foreground hover:bg-accent transition-colors text-left"
              >
                <div className="w-4 h-4 rounded border border-input flex items-center justify-center flex-shrink-0">
                  {selected.includes(value) && <Check className="w-3 h-3 text-primary" />}
                </div>
                {getLabel(item)}
              </button>
            );
          })}
          {showAddCustom && (
            <button
              type="button"
              onClick={handleAddCustom}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-accent transition-colors text-left border-t border-border"
            >
              <Plus className="w-4 h-4" />
              Add "{search.trim()}"
            </button>
          )}
        </PopoverContent>
      )}
    </Popover>
  );
}
